/**
 * Проверяет, дал ли пользователь согласие на использование cookie указанной
 * категории в cookie consent.
 *
 * @param {string} category Категория cookie из `cc_cookie`.
 * @returns {boolean} `true`, если категория разрешена пользователем.
 */
export function canUseCookies(category) {
  try {
    const raw = readCookieImpl('cc_cookie')
    if (!raw) {
      console.error(
        `cc_cookie not found — no consent for category: ${category}`
      )
      return false
    }
    const consent = JSON.parse(raw)
    const accepted =
      Array.isArray(consent.categories) && consent.categories.includes(category)
    return accepted
  } catch {
    return false
  }
}

/**
 * Оборачивает функцию работы с cookie проверкой пользовательского согласия.
 *
 * @template {Function} T
 * @param {T} fn Функция, которую можно выполнять только после согласия.
 * @param {Object} [options={}] Настройки проверки согласия.
 * @param {string} [options.category='functionality'] Категория cookie consent.
 * @param {*|Function} [options.fallback=null] Значение или функция fallback,
 *   которые будут использованы при отсутствии согласия.
 * @returns {Function} Функция-обертка с проверкой согласия.
 */
function withCookieConsent(fn, options = {}) {
  const {
    category = 'functionality', // Категория по умолчанию
    fallback = null
  } = options

  return function (...args) {
    if (!canUseCookies(category)) {
      return typeof fallback === 'function' ? fallback(...args) : fallback
    }

    return fn(...args)
  }
}

/**
 * Записывает cookie без проверки согласия.
 *
 * @param {string} name Имя cookie.
 * @param {string} value Значение cookie.
 * @param {number} [days] Срок жизни cookie в днях.
 * @returns {void}
 */
function createCookieImpl(name, value, days) {
  let expires = ''

  if (days) {
    const date = new Date()
    date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000)
    expires = '; expires=' + date.toUTCString()
  }

  const secure = window.location?.protocol === 'https:' ? '; Secure' : ''

  document.cookie =
    encodeURIComponent(name) +
    '=' +
    encodeURIComponent(value) +
    expires +
    '; path=/; SameSite=Lax' +
    secure
}

/**
 * Читает cookie без проверки согласия.
 *
 * @param {string} name Имя cookie.
 * @returns {string|null} Значение cookie или `null`, если cookie не найдена.
 */
function readCookieImpl(name) {
  const nameEQ = encodeURIComponent(name) + '='
  const ca = document.cookie.split(';')

  for (let c of ca) {
    c = c.trim()
    if (c.indexOf(nameEQ) === 0) {
      return decodeURIComponent(c.substring(nameEQ.length))
    }
  }

  return null
}

/**
 * Удаляет cookie без проверки согласия.
 *
 * @param {string} name Имя cookie.
 * @returns {void}
 */
function eraseCookieImpl(name) {
  createCookieImpl(name, '', -1)
}

/**
 * Записывает cookie, если пользователь разрешил категорию `functionality`.
 *
 * @type {(name: string, value: string, days?: number) => void|null}
 */
export const createCookie = withCookieConsent(createCookieImpl, {
  category: 'functionality',
  fallback: () => {
    console.warn(
      'Cannot create cookie because user has not given consent for functionality cookies.'
    )
    return null
  }
})

/**
 * Читает cookie, если пользователь разрешил категорию `functionality`.
 *
 * @type {(name: string) => string|null}
 */
export const readCookie = withCookieConsent(readCookieImpl, {
  category: 'functionality',
  fallback: () => {
    console.warn(
      'Cannot read cookie because user has not given consent for functionality cookies.'
    )
    return null
  }
})

/**
 * Удаляет cookie, если пользователь разрешил категорию `functionality`.
 *
 * @type {(name: string) => void|null}
 */
export const eraseCookie = withCookieConsent(eraseCookieImpl, {
  category: 'functionality',
  fallback: (name) => eraseCookieImpl(name) // Удаляем cookie даже без согласия, чтобы не оставлять следы
})
